import { useStripe } from '@stripe/react-stripe-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { useBarcodeScanner } from 'src/hooks/useBarcodeScanner';
import { Cart, CartItem, PaymentOption } from 'src/models/cart/_types';
import {
  checkCoupon,
  confirmCart,
  confirmTerminalPayment,
  getIntent,
  updateGuestInformation,
} from 'src/models/cart/cart';
import { isTerminalPaymentMethod } from 'src/models/cart/utils';
import { Coupon } from 'src/models/coupon/_types';
import { Group } from 'src/models/group/_types';
import { fetchGroup, fetchGroupsByParams } from 'src/models/group/group';
import { Location } from 'src/models/location/_types';
import { fetchLocation } from 'src/models/location/location';
import { Package } from 'src/models/package/_types';
import {
  fetchPackage,
  fetchPackagesByParams,
} from 'src/models/package/package';
import { PaymentMethod } from 'src/models/payment-methods/_types';
import {
  PosConfiguration,
  PosConfigurationItem,
  PosType,
} from 'src/models/pos-configuration/_types';
import {
  fetchPosConfiguration,
  posConfigurationQueryKey,
  setupPosConfigurationPin,
  validatePosConfigurationPin,
} from 'src/models/pos-configuration/pos-configuration';
import { Product, ProductVariant } from 'src/models/product/_types';
import {
  fetchProduct,
  fetchProductVariant,
  fetchProductsByParams,
} from 'src/models/product/product';
import {
  constrainQuantityToStock,
  getStockAvailabilityMessage,
} from 'src/models/product/utils';
import { Terminal } from 'src/models/terminal/_types';
import { getTerminal } from 'src/models/terminal/terminal';
import { User } from 'src/models/user/_types';
import {
  WorkoutPlan,
  fetchWorkoutPlan,
  fetchWorkoutPlansByParams,
} from 'src/models/workout-plan/workout-plan';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';

interface CartSummary {
  subtotal: number;
  tax: number;
  taxes: Map<string, number>;
  total: number;
  accountBalance: number;
  discount: number;
  discounts: Map<string, number>;
  currency: string;
}

interface PosContextValue {
  isLoadingProducts: boolean;
  isLoadingIntent: boolean;
  page: number;
  handlePageChange: (page: number) => void;
  search: string;
  handleSearchChange: (search: string) => void;
  config: PosConfiguration | undefined;
  selectedTerminal: Terminal | null;
  setSelectedTerminal: (terminal: Terminal | null) => void;
  shouldShowTerminalDialog: boolean;
  setShouldShowTerminalDialog: (show: boolean) => void;
  posLocation: Location | undefined;
  // Reader connection states
  isReaderConnecting: boolean;
  readerConnectionError: string | null;
  retryReaderConnection: () => void;
  getItemConfig: (itemId: Identifier) => PosConfigurationItem | undefined;
  getItem: (
    itemId: Identifier,
    itemType: string
  ) => Product | ProductVariant | Group | Package | WorkoutPlan | null;
  addToCart: (
    item: any,
    options?: {
      type?: string;
      quantity?: number;
      paymentOption?: PaymentOption;
      metadata?: Record<string, any>;
    }
  ) => Promise<void>;
  removeFromCart: (cartItemIndex: number) => void;
  updateQuantity: (cartItemIndex: number, quantity: number) => Promise<void>;
  totalItems: number;
  totalPrice: number;
  getProductsPage: () => (
    | Product
    | ProductVariant
    | Group
    | Package
    | WorkoutPlan
    | Collection
    | null
    | undefined
  )[];
  getItems: (
    itemType: string,
    tagName: string
  ) => Product[] | Package[] | Group[] | WorkoutPlan[] | undefined;
  cart: Cart | null;
  savedCart: Cart | null;
  isInCart: (itemId: Identifier) => boolean;
  applyDiscount: (discountCode: string) => void;
  removeDiscount: (discountCode: string) => void;
  isLoadingDiscount: boolean;
  isErrorDiscount: boolean;
  selectedUser: User;
  setSelectedUser: (user: User) => void;
  showWelcomeDialog: boolean;
  setShowWelcomeDialog: (show: boolean) => void;
  showFindAccountDialog: boolean;
  setShowFindAccountDialog: (show: boolean) => void;
  isInStock: (itemId: Identifier, itemType: string) => boolean;
  barcode: string;
  paymentMethod: PaymentMethod;
  handleChangePaymentMethod: (paymentMethod: PaymentMethod | null) => void;
  paymentMethodsDialogOpen: boolean;
  setPaymentMethodsDialogOpen: (open: boolean) => void;
  addDiscountDialogOpen: boolean;
  setAddDiscountDialogOpen: (open: boolean) => void;
  charge: () => Promise<void>;
  isProcessingPayment: boolean;
  setIsProcessingPayment: (isProcessing: boolean) => void;
  handleReset: () => void;
  handleGuestUser: () => void;
  isGuestCheckout: boolean;
  // PIN-related state
  isPinValidated: boolean;
  showPinDialog: boolean;
  setShowPinDialog: (show: boolean) => void;
  validatePin: (pin: string) => void;
  isValidatingPin: boolean;
  clearPin: () => void;
  // Payment PIN validation state
  showPaymentPinDialog: boolean;
  setShowPaymentPinDialog: (show: boolean) => void;
  validatePaymentPin: (pin: string) => void;
  isValidatingPaymentPin: boolean;
  // Setup Payment PIN validation state
  setupPaymentPin: ({
    pin,
    password,
  }: {
    pin: string;
    password?: string;
  }) => void;
  isSettingUpPaymentPin: boolean;
  // Kiosk staff PIN validation state
  isKioskStaffPinValidated: boolean;
  showKioskStaffPinDialog: boolean;
  setShowKioskStaffPinDialog: (show: boolean) => void;
  validateKioskStaffPin: (pin: string) => void;
  isValidatingKioskStaffPin: boolean;
  posType?: PosType;
  guestCheckoutEnabled: boolean;
  // Guest info update
  updateGuestInfo: (
    guestEmail: string,
    guestFirstName: string,
    guestLastName: string,
    sendReceipt: boolean
  ) => Promise<void>;
  shouldDisableCheckout: boolean;
  paymentCreated: boolean;
  // Service balance info
  cartSummary: CartSummary;
}

// Create context
const PosContext = createContext<PosContextValue | undefined>(undefined);

// Provider component
interface PosProviderProps {
  children: ReactNode;
  posLocationId: string;
  configId: string;
  cardReaderId: string;
  posType: PosType;
}

interface Collection {
  id: string;
  name: string;
  type: string;
  itemId: string;
  itemType: string;
  description: string;
  paymentOptions: never[];
}

export function PosProvider({
  children,
  posLocationId,
  configId,
  cardReaderId,
  posType,
}: PosProviderProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pointOfSale',
  });

  const { session } = useOptionalSession();
  const stripe = useStripe();
  const navigate = useNavigate();
  const { updateParam, params, filterParams } = useListSearchParams();
  const queryClient = useQueryClient();
  const page = params.page;
  const search = params.search;
  const barcode = filterParams.barcode;
  const [products] = useState<Map<string, Product>>(new Map());
  const [packages] = useState<Map<string, Package>>(new Map());
  const [groups] = useState<Map<string, Group>>(new Map());
  const [plans] = useState<Map<string, WorkoutPlan>>(new Map());

  const [selectedTerminal, setSelectedTerminal] = useState<Terminal | null>(
    null
  );

  const { data: posConfig, error: posConfigError } = useQuery<PosConfiguration>(
    {
      queryKey: [posConfigurationQueryKey(configId)],
      queryFn: () => fetchPosConfiguration(configId, posType),
      enabled: !!configId,
    }
  );

  // Handle posConfig errors
  useEffect(() => {
    if (posConfigError) {
      if ((posConfigError as any)?.status === 404) navigate('/not-found');
      toast.error(t('posConfigurationNotFound'), {
        position: 'bottom-left',
      });
    } else if (posConfig) {
      setCart((prevCart) => ({
        ...prevCart,
        posConfigurationId: posConfig?.id,
      }));
    }
  }, [posConfigError, navigate, t, posConfig?.id, posConfig]);

  const guestCheckoutEnabled = useMemo(
    () =>
      posType === 'pos' ||
      (posType === 'kiosk' && (session?.enableGuestCheckout || false)),
    [session?.enableGuestCheckout, posType]
  );

  useEffect(() => {
    if (posType === 'kiosk' && session) {
      // Ensure only one dialog is shown at a time
      setShowWelcomeDialog(false);
      setShowFindAccountDialog(false);

      // If guest checkout is not enabled, skip welcome dialog and go directly to find account
      if (!guestCheckoutEnabled) {
        setShowFindAccountDialog(true);
      } else {
        setShowWelcomeDialog(true);
      }
    }
  }, [posType, guestCheckoutEnabled, session]);

  const { data: posLocation } = useQuery({
    queryKey: ['locations', posLocationId],
    queryFn: () => fetchLocation(posLocationId),
    enabled: !!posLocationId,
  });

  const guestUser = useMemo(
    () =>
      ({
        firstName: 'Guest',
      } as User),
    []
  );

  const swipeCardPaymentMethod = useMemo(
    () =>
      ({
        type: 'card_present',
        paymentMethodType: 'card_present',
        id: selectedTerminal?.id,
      } as PaymentMethod),
    [selectedTerminal?.id]
  );

  const [shouldShowTerminalDialog, setShouldShowTerminalDialog] =
    useState(false);

  const [cart, setCart] = useState<Cart>(() => ({
    userId: '',
    cartItems: [],
    locationId: posLocation?.id,
    guestCheckout: true,
    paymentMethod: swipeCardPaymentMethod,
    posConfigurationId: posConfig?.id,
  }));
  const [savedCart, setSavedCart] = useState<Cart | null>(null);
  const [selectedUser, setSelectedUserState] = useState<User>(guestUser);

  // Wrapper function to handle user selection and cart intent invalidation
  const setSelectedUser = useCallback(
    (user: User) => {
      // Remove cart intent queries when user changes to prevent payment intent reuse
      queryClient.removeQueries({ queryKey: ['cartIntent'] });
      setSelectedUserState(user);
    },
    [queryClient]
  );
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const [showFindAccountDialog, setShowFindAccountDialog] = useState(false);

  // Reader connection states
  const [isReaderConnecting, setIsReaderConnecting] = useState(false);
  const [readerConnectionError, setReaderConnectionError] = useState<
    string | null
  >(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    swipeCardPaymentMethod
  );
  const [paymentMethodsDialogOpen, setPaymentMethodsDialogOpen] =
    useState(false);
  const [addDiscountDialogOpen, setAddDiscountDialogOpen] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // PIN-related state
  const [isPinValidated, setIsPinValidated] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const kioskIdleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Payment PIN validation state
  const [showPaymentPinDialog, setShowPaymentPinDialog] = useState(false);

  // Kiosk staff PIN validation state
  const [isKioskStaffPinValidated, setIsKioskStaffPinValidated] =
    useState(false);
  const [showKioskStaffPinDialog, setShowKioskStaffPinDialog] = useState(false);

  // Payment Confirmation state
  const [paymentCreated, setPaymentCreated] = useState(false);

  const collections: Collection[] = useMemo(() => {
    const packageTags = session?.packageTags || [];
    const productTags = session?.productTags || [];

    return [
      {
        id: 'all-packages',
        name: 'All Packages',
        type: 'package',
        itemId: 'all-packages',
        itemType: 'package',
        description: 'Browse all available packages',
        paymentOptions: [],
      },
      ...packageTags.map((tag, index) => ({
        id: `package-tag-${index}`,
        name: tag,
        type: 'package',
        itemId: `package-tag-${index}`,
        itemType: 'package',
        description: `Packages tagged with "${tag}"`,
        paymentOptions: [],
      })),
      {
        id: 'all-products',
        name: 'All Products',
        type: 'product',
        itemId: 'all-products',
        itemType: 'product',
        description: 'Browse all available products',
        paymentOptions: [],
      },
      ...productTags.map((tag, index) => ({
        id: `product-tag-${index}`,
        name: tag,
        type: 'product',
        itemId: `product-tag-${index}`,
        itemType: 'product',
        description: `Products tagged with "${tag}"`,
        paymentOptions: [],
      })),
      {
        id: 'all-groups',
        name: 'All Groups',
        type: 'group',
        itemId: 'all-groups',
        itemType: 'group',
        description: 'Browse all available groups',
        paymentOptions: [],
      },
      {
        id: 'all-plans',
        name: 'All Plans',
        type: 'plan',
        itemId: 'all-plans',
        itemType: 'plan',
        description: 'Browse all available plans',
        paymentOptions: [],
      },
    ];
  }, [session]);

  // Fetch all products
  const {
    data: productsData,
    isFetching: isFetchingProducts,
    isLoading: isLoadingAllProducts,
  } = useQuery({
    queryKey: ['products', 'checkout'],
    queryFn: () =>
      fetchProductsByParams({
        fetch_all: true,
        q: {
          has_publishing_channel: posType,
        },
      }),
  });

  useEffect(() => {
    if (productsData && !isFetchingProducts && !isLoadingAllProducts) {
      productsData.forEach((product) => {
        products.set(product.id.toString(), product);
      });
    }
  }, [isFetchingProducts, isLoadingAllProducts, products, productsData]);

  // Fetch all packages
  const {
    data: packagesData,
    isFetching: isFetchingPackages,
    isLoading: isLoadingAllPackages,
  } = useQuery({
    queryKey: ['packages'],
    queryFn: () =>
      fetchPackagesByParams({
        fetch_all: true,
      }),
  });

  useEffect(() => {
    if (packagesData && !isFetchingPackages && !isLoadingAllPackages) {
      packagesData.forEach((packageItem) => {
        packages.set(packageItem.id.toString(), packageItem);
      });
    }
  }, [isFetchingPackages, isLoadingAllPackages, packages, packagesData]);

  // Fetch all groups
  const {
    data: groupsData,
    isFetching: isFetchingGroups,
    isLoading: isLoadingAllGroups,
  } = useQuery({
    queryKey: ['groups'],
    queryFn: () =>
      fetchGroupsByParams({
        fetch_all: true,
        // pluck_columns: 'groups.id,name,cached_slug',
      }),
  });

  useEffect(() => {
    if (groupsData && !isFetchingGroups && !isLoadingAllGroups) {
      groupsData.forEach((group) => {
        groups.set(group.id.toString(), group);
      });
    }
  }, [isFetchingGroups, isLoadingAllGroups, groups, groupsData]);

  // Fetch all plans
  const {
    data: plansData,
    isFetching: isFetchingPlans,
    isLoading: isLoadingAllPlans,
  } = useQuery({
    queryKey: ['plans'],
    queryFn: () =>
      fetchWorkoutPlansByParams({
        summary_only: true,
        user_created: true,
        'q[price_gt]': -1,
        fetch_all: true,
      }),
  });

  useEffect(() => {
    if (plansData && !isFetchingPlans && !isLoadingAllPlans) {
      plansData.forEach((plan) => {
        plans.set(plan.id.toString(), plan);
      });
    }
  }, [isFetchingPlans, isLoadingAllPlans, plans, plansData]);

  // Helper function to get item details based on type
  const fetchItemDetails = useCallback(
    async (
      itemId: Identifier,
      itemType: string,
      type: 'item' | 'collection'
    ) => {
      if (type === 'collection') {
        const collection = collections.find(
          (collection) => collection.id === itemId
        );
        return collection;
      }

      switch (itemType.toLowerCase()) {
        case 'group':
          return await fetchGroup({ id: itemId });
        case 'fbm::package':
          return await fetchPackage({ id: itemId });
        case 'package':
          return await fetchPackage({ id: itemId });
        case 'exercisecom::product':
          return await fetchProduct({ id: itemId });
        case 'product':
          return await fetchProduct({ id: itemId });
        case 'product-variant':
          return await fetchProductVariant({ id: itemId });
        case 'workoutplan':
          return await fetchWorkoutPlan({ id: itemId });
        case 'plan':
          return await fetchWorkoutPlan({ id: itemId });
        default:
          throw new Error(`Unsupported item type: ${itemType}`);
      }
    },
    [collections]
  );

  const isLoadingProducts = useMemo(() => {
    return (
      isLoadingAllProducts ||
      isFetchingProducts ||
      isLoadingAllPackages ||
      isFetchingPackages ||
      isLoadingAllGroups ||
      isFetchingGroups ||
      isLoadingAllPlans ||
      isFetchingPlans
    );
  }, [
    isLoadingAllProducts,
    isFetchingProducts,
    isLoadingAllPackages,
    isFetchingPackages,
    isLoadingAllGroups,
    isFetchingGroups,
    isLoadingAllPlans,
    isFetchingPlans,
  ]);

  const getProductVariant = useCallback(
    (itemId: Identifier) => {
      const product = Array.from(products.values()).find((product) =>
        product.productVariants?.some(
          (variant) => variant.id.toString() === itemId.toString()
        )
      );
      if (product) {
        const variant = product.productVariants?.find(
          (variant) => variant.id.toString() === itemId.toString()
        );
        return variant || null;
      }
      return null;
    },
    [products]
  );

  const getProductsPage = () => {
    if (!posConfig?.config) return [];

    if (!search) {
      const productsPage = posConfig.config[page - 1];
      const populatedItems = productsPage.map((item) => {
        if (item.type === 'collection') {
          return collections.find(
            (collection) => collection.id === item.itemId
          );
        } else {
          if (item.itemType === 'product') {
            return products.get(item.itemId!.toString());
          }
          if (item.itemType === 'product-variant') {
            return getProductVariant(item.itemId!);
          }
          if (item.itemType === 'package') {
            return packages.get(item.itemId!.toString());
          }
          if (item.itemType === 'group') {
            return groups.get(item.itemId!.toString());
          }
          if (item.itemType === 'plan') {
            return plans.get(item.itemId!.toString());
          }
        }

        return null;
      });

      return populatedItems;
    }

    const allItemsInConfig = posConfig?.config?.flat();

    const populatedItemsInCOnfig = allItemsInConfig
      .filter((item) => item.type !== 'collection')
      .map((item) => {
        const itemId = item.itemId?.toString();

        if (item.itemType === 'product') {
          return products.get(itemId!);
        }
        if (item.itemType === 'product-variant') {
          return getProductVariant(itemId!);
        }
        if (item.itemType === 'package') {
          return packages.get(itemId!);
        }
        if (item.itemType === 'group') {
          return groups.get(itemId!);
        }
        if (item.itemType === 'plan') {
          return plans.get(itemId!);
        }
        return null;
      });

    const filteredItems = populatedItemsInCOnfig.filter((item) => {
      const nameMatches = (item?.name || '')
        .toLocaleLowerCase()
        .includes(search.toLocaleLowerCase());

      let barcodeMatches = false;

      if (item?.['productVariants'] && item['productVariants'].length > 0) {
        barcodeMatches = item['productVariants'].some((variant) =>
          variant.barcode?.includes(search)
        );
      } else {
        barcodeMatches = item?.['barcode']?.includes(search);
      }

      return nameMatches || barcodeMatches;
    });

    return filteredItems;
  };

  const {
    mutate: applyDiscount,
    isPending: isLoadingDiscount,
    data: discountData,
    isError: isErrorDiscount,
    reset: resetDiscount,
  } = useMutation({
    mutationFn: (discountCode: string) => {
      return checkCoupon(cart!, discountCode);
    },
  });

  const removeDiscount = useCallback(
    (discountCode: string) => {
      setCart((prevCart) => {
        if (!prevCart) return prevCart;
        const updatedCart: typeof prevCart = {
          ...prevCart,
          cartItems: prevCart.cartItems.map((item) => {
            if (item.coupon?.code === discountCode) {
              // Remove the coupon property from the item
              // Set coupon to undefined instead of null to match CartItem type
              const rest = Object.fromEntries(
                Object.entries(item).filter(([key]) => key !== 'coupon')
              );
              return rest as CartItem;
            }
            return item;
          }),
        };
        return updatedCart;
      });
    },
    [setCart]
  );

  // Update cart with selected user, payment method, and location id
  useEffect(() => {
    setCart((prevCart) => {
      let updatedCart = prevCart;
      // Update the user id if it changes
      if (selectedUser.id && prevCart.userId !== selectedUser.id) {
        updatedCart = {
          ...updatedCart,
          userId: selectedUser.id,
          cartItems: [],
          guestCheckout: false,
          posConfigurationId: posConfig?.id,
        };
      }

      // Update the payment method if it changes
      if (
        paymentMethod.type !== 'card_present' &&
        updatedCart.paymentMethod !== paymentMethod
      ) {
        updatedCart = { ...updatedCart, paymentMethod };
      } else if (paymentMethod.type === 'card_present') {
        updatedCart = {
          ...updatedCart,
          paymentMethod: swipeCardPaymentMethod,
        };
      }

      // Update the location id if it changes
      if (prevCart.locationId !== posLocation?.id) {
        updatedCart = { ...updatedCart, locationId: posLocation?.id };
      }
      return updatedCart;
    });
  }, [
    selectedUser,
    paymentMethod,
    swipeCardPaymentMethod,
    posLocation,
    posConfig?.id,
  ]);

  // Retry reader connection function
  const retryReaderConnection = useCallback(() => {
    if (cardReaderId) {
      setIsReaderConnecting(true);
      setReaderConnectionError(null);
      // This will trigger the query to re-run
      queryClient.invalidateQueries({
        queryKey: ['card-reader', cardReaderId, posLocation?.id],
      });
    }
  }, [cardReaderId, posLocation?.id, queryClient]);

  // Validate card reader
  const {
    isLoading: isReaderLoading,
    data: cardReaderData,
    error: cardReaderError,
  } = useQuery({
    queryKey: ['card-reader', cardReaderId, posLocation?.id],
    queryFn: () => getTerminal(cardReaderId),
    enabled: !!cardReaderId && !!posLocation,
    retry: 1,
  });

  useEffect(() => {
    if (cardReaderError) {
      setIsReaderConnecting(false);
      setSelectedTerminal(null);
      setReaderConnectionError('Failed to connect to reader');
      setShouldShowTerminalDialog(true);
    }
  }, [cardReaderError]);

  useEffect(() => {
    if (cardReaderData && !isReaderLoading) {
      setIsReaderConnecting(false);
      if (
        cardReaderData.status === 'online' &&
        cardReaderData.locationId === posLocation?.id
      ) {
        setSelectedTerminal(cardReaderData);
        setReaderConnectionError(null);
        setShouldShowTerminalDialog(false); // Hide dialog if reader is valid
      } else {
        setSelectedTerminal(null);
        setReaderConnectionError('Reader is offline or location mismatch');
        setShouldShowTerminalDialog(true);
      }
    }
  }, [
    cardReaderId,
    selectedTerminal,
    isReaderLoading,
    cardReaderData,
    posLocation?.id,
  ]);

  const getItemConfig = useCallback(
    (itemId: Identifier) => {
      if (!itemId) return undefined;

      return posConfig?.config
        ?.flat()
        .find((item) => item.itemId === itemId.toString());
    },
    [posConfig?.config]
  );

  const getItem = useCallback(
    (itemId: Identifier, itemType: string) => {
      let item:
        | Product
        | ProductVariant
        | Group
        | Package
        | WorkoutPlan
        | null = null;

      if (itemType === 'product-variant') {
        item = getProductVariant(itemId) ?? null;
      } else if (itemType === 'product') {
        item = products.get(itemId.toString()) ?? null;
      } else if (itemType === 'package') {
        item = packages.get(itemId.toString()) ?? null;
      } else if (itemType === 'group') {
        item = groups.get(itemId.toString()) ?? null;
      } else if (itemType === 'plan') {
        // For 'plan', try to get the WorkoutPlan by id, fallback to null if not found
        item = plans?.get?.(itemId.toString?.()) ?? null;
      }
      return item;
    },
    [getProductVariant, groups, packages, plans, products]
  );

  const getItems = useCallback(
    (itemType: string, tagName: string) => {
      if (itemType === 'product') {
        return Array.from(products.values()).filter((product) =>
          tagName
            ? product.tags?.some(
                (tag) => tag.toLowerCase() === tagName.toLowerCase()
              )
            : true
        );
      } else if (itemType === 'package') {
        return Array.from(packages.values()).filter((pkg) =>
          tagName
            ? pkg['tags']?.some(
                (tag) => tag.toLowerCase() === tagName.toLowerCase()
              )
            : true
        );
      } else if (itemType === 'group') {
        return Array.from(groups.values());
      } else if (itemType === 'plan') {
        return Array.from(plans.values()).filter((plan) =>
          tagName
            ? plan['tags']?.some(
                (tag) => tag.toLowerCase() === tagName.toLowerCase()
              )
            : true
        );
      }
    },
    [products, packages, groups, plans]
  );

  // Add item to cart
  const addToCart = useCallback(
    async (
      item: any,
      options: {
        type?: string;
        quantity?: number;
        paymentOption?: PaymentOption;
        metadata?: Record<string, any>;
      } = {}
    ) => {
      const { type = 'product-variant', quantity = 1, paymentOption } = options;

      // Map frontend types to backend types
      let itemType: string;
      let itemId: Identifier;

      if (type === 'product-variant' && item.id) {
        itemType = 'ProductVariant';
        itemId = item.id;
      } else if (type === 'product' && item.id) {
        itemType = 'Exercisecom::Product';
        itemId = item.id;
      } else if (type === 'group' && item.id) {
        itemType = 'Group';
        itemId = item.id;
      } else if (type === 'package' && item.id) {
        itemType = 'Fbm::Package';
        itemId = item.id;
      } else if (type === 'plan' && item.id) {
        itemType = 'WorkoutPlan';
        itemId = item.id;
      } else {
        toast.error('Invalid item type or missing ID', {
          position: 'bottom-left',
        });
        return;
      }

      try {
        // Fetch item details if needed
        let itemDetails = item;
        if (!item?.paymentOptions || itemType === 'product-variant') {
          itemDetails = await fetchItemDetails(itemId, itemType, 'item');
        }

        // Create default payment option if none exist
        let selectedPaymentOption = paymentOption;
        if (!selectedPaymentOption && itemDetails.paymentOptions?.length > 0) {
          selectedPaymentOption = itemDetails.paymentOptions[0];
        } else if (!selectedPaymentOption) {
          selectedPaymentOption = {
            type: 'purchase',
            amount: itemDetails.price || 0,
            currency: 'usd',
            name: 'One Time Purchase',
            location: null,
          };
        }

        const newCartItem: CartItem = {
          quantity,
          item: {
            ...itemDetails,
            paymentOptions: itemDetails.paymentOptions || [
              selectedPaymentOption,
            ],
            paymentOption: selectedPaymentOption,
            imageUrl: itemDetails.imageUrl || '',
            name: itemDetails.name || itemDetails.displayName || '',
          },
          name: itemDetails.name || itemDetails.displayName || '',
          itemId,
          itemType,
          paymentOption: selectedPaymentOption,
        };

        const currentCart = cart || { cartItems: [] };

        // Check if item already exists
        const existingItemIndex = currentCart.cartItems.findIndex(
          (cartItem) =>
            cartItem.itemId === itemId &&
            cartItem.itemType === itemType &&
            JSON.stringify(cartItem.paymentOption) ===
              JSON.stringify(selectedPaymentOption)
        );

        let updatedCartItems;
        if (existingItemIndex >= 0) {
          // Update existing item quantity
          updatedCartItems = [...currentCart.cartItems];
          updatedCartItems[existingItemIndex] = {
            ...updatedCartItems[existingItemIndex],
            quantity: updatedCartItems[existingItemIndex].quantity + quantity,
          };
        } else {
          // Add new item
          updatedCartItems = [...currentCart.cartItems, newCartItem];
        }

        const updatedCart = {
          ...currentCart,
          cartItems: updatedCartItems,
        };

        setCart(updatedCart as Cart);
        toast.success('Item added to cart successfully!', {
          position: 'bottom-left',
        });
      } catch (error: any) {
        toast.error(error?.message || 'Failed to add item to cart', {
          position: 'bottom-left',
        });
      }
    },
    [cart, fetchItemDetails]
  );

  // Remove item from cart
  const removeFromCart = useCallback(
    (cartItemIndex: number) => {
      if (!cart) return;

      const updatedCartItems = cart.cartItems.filter(
        (_, index) => index !== cartItemIndex
      );
      const updatedCart = { ...cart, cartItems: updatedCartItems };
      setCart(updatedCart);
    },
    [cart]
  );

  // Update item quantity
  const updateQuantity = useCallback(
    async (cartItemIndex: number, quantity: number) => {
      if (!cart) return;

      if (quantity <= 0) {
        removeFromCart(cartItemIndex);
        return;
      }

      const cartItem = cart.cartItems[cartItemIndex];
      let constrainedQuantity = quantity;

      // For ProductVariant items, validate against available stock
      if (cartItem.itemType === 'ProductVariant' && cartItem.itemId) {
        try {
          // Fetch the product variant to get current stock levels
          const variant = await fetchProductVariant({ id: cartItem.itemId });

          // Constrain quantity to available stock
          constrainedQuantity = constrainQuantityToStock(
            variant,
            quantity,
            posLocation?.id,
            cart,
            cartItemIndex
          );

          // Show warning if quantity was reduced due to stock limitations
          if (constrainedQuantity < quantity) {
            const stockMessage = getStockAvailabilityMessage(
              variant,
              posLocation?.id,
              cart,
              cartItemIndex
            );

            const message = stockMessage
              ? `Quantity reduced to ${constrainedQuantity}. ${stockMessage}`
              : `Quantity reduced to ${constrainedQuantity} due to stock limitations.`;

            toast.error(message, {
              position: 'bottom-left',
            });
          }
        } catch (error) {
          toast.error(
            `Failed to validate stock for product variant: ${error}`,
            {
              position: 'bottom-left',
            }
          );
        }
      }

      const updatedCartItems = [...cart.cartItems];
      updatedCartItems[cartItemIndex] = {
        ...updatedCartItems[cartItemIndex],
        quantity: constrainedQuantity,
      };

      const updatedCart = { ...cart, cartItems: updatedCartItems };
      setCart(updatedCart);
    },
    [cart, posLocation?.id, removeFromCart]
  );

  // Calculate cart totals
  const { totalItems, totalPrice } = useMemo(() => {
    if (!cart?.cartItems) {
      return { totalItems: 0, totalPrice: 0 };
    }

    const items = cart.cartItems.reduce(
      (total, item) => total + item.quantity,
      0
    );
    const price = cart.cartItems.reduce(
      (total, item) =>
        total + (item.paymentOption?.amount || 0) * item.quantity,
      0
    );

    return { totalItems: items, totalPrice: price };
  }, [cart]);

  // Check if item is in cart
  const isInCart = useCallback(
    (itemId: Identifier) => {
      if (!itemId) return false;

      return (
        cart?.cartItems.some(
          (item) => item.itemId?.toString() === itemId.toString()
        ) || false
      );
    },
    [cart]
  );

  const isInStock = useCallback(
    (itemId: Identifier, itemType: string) => {
      if (itemType === 'product-variant') {
        const productVariant = getProductVariant(itemId);

        if (!productVariant) return false;

        const inventoryByLocation = productVariant?.inventoryLevels?.find(
          (inventory) => inventory.locationId === posLocation?.id
        );

        if (!inventoryByLocation) return false;

        return inventoryByLocation.available > 0;
      } else {
        return true;
      }
    },
    [posLocation, getProductVariant]
  );

  const {
    data: intent,
    isFetching: isFetchingIntent,
    isLoading: isLoadingIntentData,
    error: intentError,
  } = useQuery<Cart | null>({
    queryKey: ['cartIntent', cart?.id, cart?.cartItems, cart?.paymentMethod],
    queryFn: () => {
      if (cart.cartItems.length > 0) {
        cart.paymentMethod = paymentMethod;
        return getIntent(cart);
      } else {
        return null;
      }
    },
    enabled: !!cart && cart.cartItems.length > 0,
  });

  useEffect(() => {
    if (intentError) {
      toast.error(api.formatError(intentError, 'Failed to get cart intent'), {
        position: 'bottom-left',
      });
    }
  }, [intentError]);

  useEffect(() => {
    if (intent) {
      setSavedCart(intent);
    }
  }, [intent]);

  // Apply coupon to cart items
  useEffect(() => {
    if (!discountData?.coupon) return;
    const hasAnyValidItems = discountData?.items.some((item) => item.valid);

    if (hasAnyValidItems) {
      setCart((prev) => {
        const updatedCartItems = [...prev.cartItems];
        const firstValidItemWithoutDiscountIndex = updatedCartItems.findIndex(
          (item) =>
            !item.coupon &&
            discountData.items.findIndex(
              (discountItem) =>
                discountItem.valid &&
                item.itemId?.toString() === discountItem.itemId?.toString()
            ) !== -1
        );
        if (firstValidItemWithoutDiscountIndex !== -1) {
          updatedCartItems[firstValidItemWithoutDiscountIndex].coupon =
            discountData.coupon as unknown as Coupon;
        }

        return {
          ...prev,
          cartItems: updatedCartItems,
        };
      });
    }
  }, [discountData, setCart]);

  const isLoadingIntent = useMemo(() => {
    return isLoadingIntentData || isFetchingIntent;
  }, [isLoadingIntentData, isFetchingIntent]);

  //Summary of cart totals
  const cartSummary = useMemo(() => {
    let subTotal = 0;
    let tax = 0;
    const taxes = new Map<string, number>();
    let total = 0;
    let accountBalance = 0;
    let discount = 0;
    const discounts = new Map<string, number>();
    const currency = intent?.currency || 'USD';

    if (intent?.priceBreakdown) {
      intent.priceBreakdown.forEach((item) => {
        subTotal += item.price;
        total += item.total;
        accountBalance += item.balanceAdjustment;
        discount += item.discount;
        tax += item.tax;
        // Add discount to discounts map if coupon code and discount are present
        if (item.couponCode && item.discount) {
          if (discounts.has(item.couponCode)) {
            const currentDiscount = discounts.get(item.couponCode) || 0;

            discounts.set(item.couponCode, currentDiscount + item.discount);
          } else {
            discounts.set(item.couponCode, item.discount);
          }
        }

        // Add taxes to taxes map
        if (item.taxesBreakdown) {
          item.taxesBreakdown.forEach((tax) => {
            if (taxes.has(tax.label)) {
              const currentTax = taxes.get(tax.label) || 0;
              taxes.set(tax.label, currentTax + tax.amount);
            } else {
              taxes.set(tax.label, tax.amount);
            }
          });
        }
      });
    }

    return {
      subtotal: subTotal,
      tax: tax,
      taxes: taxes,
      total: total,
      accountBalance: accountBalance,
      discount: discount,
      discounts: discounts,
      currency: currency,
    };
  }, [intent]);

  const handleSearchChange = useCallback((search: string) => {
    updateParam('barcode', '');
    updateParam('page', 1);
    updateParam('search', search);
  }, [updateParam]);

  // Barcode scanner - when a barcode is scanned, replace the search field content
  useBarcodeScanner({
    onScan: (barcode) => {
      // Directly set the barcode - the SearchTextField useEffect should sync properly
      updateParam('search', barcode);
    },
    minLength: 4,
    enabled: true,
  });

  const handlePageChange = (page: number) => {
    updateParam('page', page);
  };

  const handlePayment = useCallback(async () => {
    if (!stripe || !savedCart) return;

    try {
      setIsProcessingPayment(true);

      if (savedCart?.paymentIntentSecret) {
        if (
          typeof paymentMethod === 'object' &&
          paymentMethod?.paymentMethodType === 'bacs_debit'
        ) {
          const { error } = await confirmCart(savedCart.id);
          if (error) throw error;
        } else if (isTerminalPaymentMethod(cart)) {
          const currentTerminal = selectedTerminal?.stripeId || '';
          const response = await confirmTerminalPayment(
            savedCart.id,
            currentTerminal,
            false,
            ''
          );

          if (response.error) throw new Error(response.error);
        } else {
          const { error } = await stripe.confirmPayment({
            clientSecret: savedCart.paymentIntentSecret,
            redirect: 'if_required',
            confirmParams: {
              payment_method:
                typeof paymentMethod === 'string'
                  ? paymentMethod
                  : session?.destinationCharges
                  ? paymentMethod?.processorId
                  : paymentMethod?.subProcessorId,
            },
          });

          if (error) throw error;
        }
      } else {
        const { error } = await confirmCart(savedCart.id);
        if (error) throw error;
      }

      setPaymentCreated(true);
    } catch (error: any) {
      setIsProcessingPayment(false);
      toast.error(error?.message || t('paymentMethods.paymentError'), {
        position: 'bottom-left',
      });
    }
  }, [
    stripe,
    savedCart,
    paymentMethod,
    cart,
    selectedTerminal?.stripeId,
    session?.destinationCharges,
    t,
  ]);

  const handleReset = useCallback(() => {
    // Clear saved cart first to prevent any references to old data
    setSavedCart(null);

    // Invalidate cart intent queries BEFORE setting new cart to prevent race conditions
    queryClient.removeQueries({ queryKey: ['cartIntent'] });

    // Reset discount mutation data
    resetDiscount();

    // Reset local state
    setSelectedUserState(guestUser);
    setPaymentMethod(swipeCardPaymentMethod);
    setCart({
      userId: '',
      cartItems: [],
      locationId: posLocation?.id,
      guestCheckout: true,
      paymentMethod: swipeCardPaymentMethod,
      posConfigurationId: posConfig?.id,
    });

    // Reset dialog states - ensure only one dialog is shown at a time
    setShowWelcomeDialog(false);
    setShowFindAccountDialog(false);

    // Show welcome dialog to prompt account selection (or find account if guest checkout disabled)
    if (posType === 'kiosk') {
      // If guest checkout is not enabled, skip welcome dialog and go directly to find account
      if (!guestCheckoutEnabled) {
        setShowFindAccountDialog(true);
      } else {
        setShowWelcomeDialog(true);
      }
    }

    // Invalidate all product detail queries
    if (posConfig?.config) {
      posConfig.config.flat().forEach((item) => {
        if (item.itemId) {
          queryClient.invalidateQueries({
            queryKey: [`${item.itemType}-price`, item.itemId],
          });
        }
      });
    }

    // Reset search
    handleSearchChange('');
  }, [
    queryClient,
    guestUser,
    swipeCardPaymentMethod,
    posLocation?.id,
    posType,
    posConfig?.id,
    posConfig?.config,
    guestCheckoutEnabled,
    resetDiscount,
    handleSearchChange,
  ]);

  const handleGuestUser = useCallback(() => {
    // Remove cart intent queries to prevent race conditions
    queryClient.removeQueries({ queryKey: ['cartIntent'] });

    setSelectedUserState(guestUser);
    setPaymentMethod(swipeCardPaymentMethod);
    setCart({
      userId: '',
      locationId: posLocation?.id,
      guestCheckout: true,
      guestEmail: '',
      guestFirstName: '',
      guestLastName: '',
      cartItems: [],
      posConfigurationId: posConfig?.id,
    });
  }, [
    guestUser,
    posLocation?.id,
    swipeCardPaymentMethod,
    queryClient,
    posConfig?.id,
  ]);

  const handleChangePaymentMethod = useCallback(
    (paymentMethod: PaymentMethod | null) => {
      if (paymentMethod) {
        setPaymentMethod(paymentMethod);
      } else {
        setPaymentMethod(swipeCardPaymentMethod);
      }
    },
    [swipeCardPaymentMethod]
  );

  const isGuestCheckout = useMemo(() => {
    if (!cart?.guestCheckout) return false;

    return cart?.guestCheckout;
  }, [cart]);

  const charge = useCallback(async () => {
    if (!cart) return;

    // Check if PIN validation is required for this payment
    // PIN required if: 1. Not guest checkout (user is signed in)
    // 2. Payment method is not card_present (using existing payment method)
    const requiresPinForPayment =
      posType === 'kiosk' && paymentMethod?.type !== 'card_present';

    if (requiresPinForPayment) {
      // Show payment PIN dialog instead of processing payment directly
      setShowPaymentPinDialog(true);
      return;
    }

    // For guest checkout or card_present payments, proceed directly
    await handlePayment();
  }, [cart, handlePayment, posType, paymentMethod?.type]);

  // PIN validation mutation
  const validatePinMutation = useMutation({
    mutationFn: (pin: string) => {
      // For kiosk mode, pass the selected user's ID for validation
      const userId =
        posType === 'kiosk' ? selectedUser?.id?.toString() : undefined;
      return validatePosConfigurationPin(posType, pin, userId);
    },
    onSuccess: () => {
      setIsPinValidated(true);
      setShowPinDialog(false);
    },
    onError: (error) => {
      toast.error(api.formatError(error, 'Failed to validate PIN'), {
        position: 'bottom-left',
      });
    },
  });

  const validatePin = validatePinMutation.mutate;
  const isValidatingPin = validatePinMutation.isPending;

  // Payment PIN validation mutation - separate from general POS PIN
  const validatePaymentPinMutation = useMutation({
    mutationFn: (pin: string) => {
      // For kiosk mode, pass the selected user's ID for validation
      const userId =
        posType === 'kiosk' ? selectedUser?.id?.toString() : undefined;
      return validatePosConfigurationPin(posType, pin, userId);
    },
    onSuccess: async () => {
      setShowPaymentPinDialog(false);
      // Proceed with payment after PIN is validated
      await handlePayment();
    },
    onError: (error) => {
      toast.error(
        api.formatError(error, 'Failed to validate PIN for payment'),
        {
          position: 'bottom-left',
        }
      );
    },
  });

  const validatePaymentPin = validatePaymentPinMutation.mutate;
  const isValidatingPaymentPin = validatePaymentPinMutation.isPending;

  // Setup Payment PIN validation mutation
  const setupPaymentPinMutation = useMutation({
    mutationFn: ({ pin, password }: { pin: string; password?: string }) => {
      const userId = selectedUser?.id?.toString();
      return setupPosConfigurationPin(pin, userId, password);
    },
    onSuccess: async () => {
      setShowPaymentPinDialog(false);
      // Proceed with payment after PIN is validated
      await handlePayment();
    },
    onError: (error) => {
      toast.error(api.formatError(error, 'Failed to setup PIN for payment'), {
        position: 'bottom-left',
      });
    },
  });

  const setupPaymentPin = setupPaymentPinMutation.mutate;
  const isSettingUpPaymentPin = setupPaymentPinMutation.isPending;

  // Kiosk staff PIN validation mutation - for unlocking kiosk interface
  const validateKioskStaffPinMutation = useMutation({
    mutationFn: (pin: string) => {
      // Always validate as staff PIN (no specific user ID needed)
      return validatePosConfigurationPin(posType, pin);
    },
    onSuccess: () => {
      setIsKioskStaffPinValidated(true);
      setShowKioskStaffPinDialog(false);
    },
    onError: (error) => {
      toast.error(api.formatError(error, 'Failed to validate staff PIN'), {
        position: 'bottom-left',
      });
    },
  });

  const validateKioskStaffPin = validateKioskStaffPinMutation.mutate;
  const isValidatingKioskStaffPin = validateKioskStaffPinMutation.isPending;

  // Guest info update mutation
  const updateGuestInfoMutation = useMutation({
    mutationFn: ({
      guestEmail,
      guestFirstName,
      guestLastName,
      sendReceipt,
    }: {
      guestEmail: string;
      guestFirstName: string;
      guestLastName: string;
      sendReceipt: boolean;
    }) => {
      if (!savedCart?.id) {
        throw new Error('No cart available for guest info update');
      }
      return updateGuestInformation(
        savedCart.id,
        guestEmail,
        guestFirstName,
        guestLastName,
        sendReceipt
      );
    },
    onSuccess: () => {
      toast.success('Guest information saved successfully', {
        position: 'bottom-left',
      });
    },
    onError: (error) => {
      toast.error(api.formatError(error, 'Failed to save guest information'), {
        position: 'bottom-left',
      });
    },
  });

  const updateGuestInfo = useCallback(
    async (
      guestEmail: string,
      guestFirstName: string,
      guestLastName: string,
      sendReceipt: boolean
    ) => {
      return updateGuestInfoMutation.mutateAsync({
        guestEmail,
        guestFirstName,
        guestLastName,
        sendReceipt,
      });
    },
    [updateGuestInfoMutation]
  );

  // Clear PIN function
  const clearPin = useCallback(() => {
    setIsPinValidated(false);
    setShowPinDialog(true);
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
  }, []);

  // Update activity time function
  const updateActivity = useCallback(() => {
    // This function is used to reset the idle timeout
    // The actual activity time tracking is handled in the timeout logic
  }, []);

  // Idle timeout effect - 10 minutes = 600,000 milliseconds
  useEffect(() => {
    // Only set up idle timeout for 'pos' type configurations
    if (!posType || posType !== 'pos') return;

    const setupIdleTimeout = () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }

      idleTimeoutRef.current = setTimeout(() => {
        clearPin();
        toast.error(
          'Session expired due to inactivity. Please enter PIN again.',
          {
            position: 'bottom-left',
          }
        );
      }, 10 * 60 * 1000); // 10 minutes
    };

    setupIdleTimeout();

    // Set up activity listeners
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    const handleActivity = () => {
      updateActivity();
      setupIdleTimeout();
    };

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [isPinValidated, posType, clearPin, updateActivity]);

  // Kiosk idle timeout effect - 5 minutes
  useEffect(() => {
    // Only set up kiosk idle timeout for 'kiosk' type configurations
    if (!posType || posType !== 'kiosk') return;

    const setupKioskIdleTimeout = () => {
      if (kioskIdleTimeoutRef.current) {
        clearTimeout(kioskIdleTimeoutRef.current);
      }

      kioskIdleTimeoutRef.current = setTimeout(() => {
        handleReset();
        toast.error(t('kioskIdleTimeout'), {
          position: 'bottom-left',
        });
      }, 5 * 60 * 1000); // 5 minutes
    };

    setupKioskIdleTimeout();

    // Set up activity listeners
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    const handleKioskActivity = () => {
      setupKioskIdleTimeout();
    };

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleKioskActivity, true);
    });

    return () => {
      if (kioskIdleTimeoutRef.current) {
        clearTimeout(kioskIdleTimeoutRef.current);
      }
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleKioskActivity, true);
      });
    };
  }, [posType, handleReset, t]);

  // Check if PIN is required and show dialog accordingly
  useEffect(() => {
    if (posType === 'pos' && !isPinValidated && !showPinDialog) {
      setShowPinDialog(true);
    }
  }, [posType, isPinValidated, showPinDialog]);

  // Check if kiosk staff PIN is required and show dialog accordingly
  useEffect(() => {
    if (
      posType === 'kiosk' &&
      !isKioskStaffPinValidated &&
      !showKioskStaffPinDialog
    ) {
      setShowKioskStaffPinDialog(true);
    }
  }, [posType, isKioskStaffPinValidated, showKioskStaffPinDialog]);

  const shouldDisableCheckout = useMemo(() => {
    return (
      !savedCart ||
      cart?.cartItems.length === 0 ||
      isLoadingIntent ||
      isFetchingIntent ||
      !!intentError
    );
  }, [
    savedCart,
    cart?.cartItems.length,
    isLoadingIntent,
    isFetchingIntent,
    intentError,
  ]);

  const contextValue: PosContextValue = {
    isLoadingProducts,
    isLoadingIntent,
    page,
    handlePageChange,
    search,
    handleSearchChange,
    config: posConfig,
    selectedTerminal,
    setSelectedTerminal,
    shouldShowTerminalDialog,
    setShouldShowTerminalDialog,
    posLocation,
    // Reader connection states
    isReaderConnecting,
    readerConnectionError,
    retryReaderConnection,
    totalItems,
    totalPrice,
    getProductsPage,
    getItems,
    cart,
    savedCart,
    addToCart,
    removeFromCart,
    updateQuantity,
    getItemConfig,
    getItem,
    isInCart,
    // Discounts
    applyDiscount,
    removeDiscount,
    isLoadingDiscount,
    isErrorDiscount,
    // User
    selectedUser,
    setSelectedUser,
    showWelcomeDialog,
    setShowWelcomeDialog,
    showFindAccountDialog,
    setShowFindAccountDialog,
    isInStock,
    barcode,
    paymentMethod,
    handleChangePaymentMethod,
    paymentMethodsDialogOpen,
    setPaymentMethodsDialogOpen,
    addDiscountDialogOpen,
    setAddDiscountDialogOpen,
    charge,
    isProcessingPayment,
    setIsProcessingPayment,
    handleReset,
    handleGuestUser,
    isGuestCheckout,
    // PIN-related state
    isPinValidated,
    showPinDialog,
    setShowPinDialog,
    validatePin,
    isValidatingPin,
    clearPin,
    // Payment PIN validation state
    showPaymentPinDialog,
    setShowPaymentPinDialog,
    validatePaymentPin,
    isValidatingPaymentPin,
    // Setup Payment PIN validation state
    setupPaymentPin,
    isSettingUpPaymentPin,
    // Kiosk staff PIN validation state
    isKioskStaffPinValidated,
    showKioskStaffPinDialog,
    setShowKioskStaffPinDialog,
    validateKioskStaffPin,
    isValidatingKioskStaffPin,
    // Guest info update
    updateGuestInfo,
    posType: posType,
    guestCheckoutEnabled,
    shouldDisableCheckout,
    // Payment Confirmation state
    paymentCreated,
    // Service balance info
    cartSummary,
  };

  return (
    <PosContext.Provider value={contextValue}>{children}</PosContext.Provider>
  );
}

export function usePosContext(): PosContextValue {
  const context = useContext(PosContext);
  if (context === undefined) {
    throw new Error('usePosContext must be used within a PosProvider');
  }
  return context;
}
